import React from "react";
import lodash from "lodash";
import { get, flatten, uniq } from "lodash-es";
import color from "color";
import { getDataFromCacheOrURL } from "./shared/getDataFromCacheOrURL";
import { processAttributes } from "./processAttributes";

const CustomizerContext = React.createContext();

CustomizerContext.displayName = "CustomizerContext";

function CustomizerContextProvider({
	children,
	definitions,
	attributes: customizerAttributes,
}) {
	let { presets, codeTemplate, defaultAttributes } = definitions;

	customizerAttributes = processAttributes(customizerAttributes);
	let { pcoplusKey, churchCenterUrl, widget } = customizerAttributes;

	let AttributesState = React.useState([...defaultAttributes]);
	let [attributes, setAttributes] = AttributesState;

	let [darkMode, setDarkMode] = React.useState(false);

	let SelectedPresetsState = React.useState(null);
	let [selectedPreset, setSelectedPreset] = SelectedPresetsState;

	let [changed, setChanged] = React.useState(false);
	let ChangeState = [changed, checkIfChanged];

	let CodeState = React.useState("");
	let [code, setCode] = CodeState;

	let InitialLoadState = React.useState(false);
	let [initialLoad, setInitialLoad] = InitialLoadState;

	let PreviewCodeState = React.useState("");
	let [previewCode, setPreviewCode] = PreviewCodeState;

	let [groupTypes, setGroupTypes] = React.useState([]);
	let [groupTags, setGroupTags] = React.useState([]);

	function generateCode({ attributes, codeTemplate }) {
		let validationErrors = [];
		let generatedCode = lodash.compact(
			attributes.map((attribute) => {
				if (attribute.disabled) return null;

				if (!attribute.value) return null;

				if (attribute.determineDarkMode) {
					if (color(attribute.value).isDark()) {
						setDarkMode(false);
					} else {
						setDarkMode(true);
					}
				}

				if (attribute.condition) {
					let hideForCondition = [];

					lodash.forEach(attribute.condition, (val, key) => {
						let matchingAttribute = lodash.find(attributes, { key: key });
						// if (lodash.get(matchingAttribute, "value") !== val)
						// 	hideForCondition.push(true);

						if (lodash.isObject(val)) {
							let { method, value } = val;

							if (method === "contains") {
								if (!lodash.get(matchingAttribute, "value", "").includes(value))
									hideForCondition.push(true);
							}
						} else if (val === "valued") {
							if (lodash.get(matchingAttribute, "value", "").length < 1)
								hideForCondition.push(true);
						} else {
							if (lodash.get(matchingAttribute, "value") !== val)
								hideForCondition.push(true);
						}
					});

					if (hideForCondition.length) return null;
				}

				if (attribute.required) {
					console.log("--", attribute.value);
					if (!attribute.value || attribute.value === "") {
						validationErrors.push(attribute);
					}
				}

				return `    ${attribute.key}="${attribute.value}"`;
			})
		).join(`
		`);

		let newCode = codeTemplate.replace("INSERTION_POINT", generatedCode);

		if (validationErrors.length) {
			newCode = "";
		}

		return newCode;
	}

	React.useEffect(() => {
		if (!selectedPreset) return;
		let preset = lodash.find(presets, { name: selectedPreset });
		console.log(preset);
		updateAttributes(preset.attributes, true);
	}, [selectedPreset]);

	React.useEffect(() => {
		if (!initialLoad) return;

		let newCode = generateCode({ codeTemplate, attributes });

		setCode(newCode);

		if (previewCode === "") setPreviewCode(newCode);
	}, [initialLoad, attributes, pcoplusKey, churchCenterUrl]);

	React.useEffect(() => {
		updateAttributes([
			{
				key: "data-pcoplus-key",
				value: pcoplusKey,
			},
			{
				key: "data-church-center-url",
				value: churchCenterUrl,
			},
		]);
		// setChanged(true);
		setInitialLoad(true);

		if (widget === "groups-customizer") {
			loadGroupData();
		}
	}, []);

	function loadGroupData() {
		let url = `https://pcochef.com/plusapi/${pcoplusKey}/groups/`;

		getDataFromCacheOrURL(url).then(([groupsData, status]) => {
			let groupsDataArray = get(groupsData, "groups", []);

			let loadedGroupTypes = uniq(
				groupsDataArray.map((group) => {
					return get(group, "attributes.group_type");
				})
			).sort();

			setGroupTypes(loadedGroupTypes);

			let loadedGroupTags = uniq(
				flatten(
					groupsDataArray.map((group) => {
						return get(group, "attributes.tags");
					})
				)
			).sort();

			setGroupTags(loadedGroupTags);
		});
	}

	function getAttributeValue(key) {
		let matchingIndex = lodash.findIndex(attributes, { key: key });

		return lodash.get(attributes, `${matchingIndex}.value`);
	}

	function refreshPreview() {
		console.log("refresh", new Date());
		setPreviewCode("");
		window.setTimeout(() => {
			setChanged(true);
			setPreviewCode(code);
		}, 300);
	}

	function checkIfChanged() {
		let isCodeChanged = code !== previewCode;

		setChanged(isCodeChanged);
	}

	function updateAttributes(newAttributes, forceChange, otherSettings = {}) {
		let { darkMode: darkModeSet } = otherSettings;

		if (darkModeSet) {
			setDarkMode(darkModeSet);
		}

		let updatedAttributes = [...attributes];

		if (!lodash.isArray(newAttributes)) newAttributes = [newAttributes];

		newAttributes.forEach((newAttribute) => {
			let { key, value } = newAttribute;

			let matchingIndex = lodash.findIndex(updatedAttributes, { key: key });

			updatedAttributes[matchingIndex] = {
				...updatedAttributes[matchingIndex],
				...{ value },
			};
		});

		setAttributes(updatedAttributes);

		if (forceChange) {
			let newCode = generateCode({
				codeTemplate: codeTemplate,
				attributes: updatedAttributes,
			});
			setCode(newCode);
			setPreviewCode(newCode);
		} else {
			checkIfChanged();
		}

		return updatedAttributes;
	}

	let value = {
		customizerAttributes,
		AttributesState,
		updateAttributes,
		CodeState,
		PreviewCodeState,
		refreshPreview,
		presets,
		codeTemplate,
		defaultAttributes,
		InitialLoadState,
		ChangeState,
		SelectedPresetsState,
		darkMode,
		groupTypes,
		groupTags,
	};

	return (
		<CustomizerContext.Provider value={value}>
			{children}
		</CustomizerContext.Provider>
	);
}

let CustomizerContextConsumer = CustomizerContext.Consumer;

export {
	CustomizerContext,
	CustomizerContextProvider,
	CustomizerContextConsumer,
};
